/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AN AXIS FOR THINGS YOU COUNT
 *
 * The sibling of `niceCeiling` in `hoursScale`, and separate from it on
 * purpose: the round numbers for a duration are 10 minutes, half an hour, four
 * hours, and the round numbers for a headcount are 2, 10, 100. One function
 * trying to be both produces an axis marked `4h` for a count of people.
 *
 * Same two rules as the hours axis, for the same two reasons:
 *
 *   ABOVE THE PEAK   a bar or a point flush with the top of a chart reads as
 *                    clipped, as though the real figure were higher.
 *
 *   EVEN             the chart prints its midpoint, and half of an odd
 *                    ceiling is a gridline labelled `2` sitting at 2.5.
 *                    There is no such thing as half a person.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Familiar round counts. Every one is even, so the midpoint is a whole number. */
const STEPS = [2, 4, 6, 10, 20, 40, 60, 100, 200, 400, 600, 1000, 2000] as const;

/**
 * The axis ceiling for a chart whose largest count is `peak`.
 *
 * An empty chart still gets an axis — `2`, the smallest step, so that a
 * platform nobody has used yet draws a flat line along the floor rather than
 * one at full height.
 */
export function niceCount(peak: number): number {
  if (peak <= 0) return STEPS[0];

  for (const step of STEPS) {
    if (peak < step) return step;
  }

  /* Past the last step, whole thousands — and `floor + 1` rather than `ceil`,
     so an organisation of exactly 2000 active people does not draw its peak
     flush against the ceiling. */
  return (Math.floor(peak / 1000) + 1) * 1000;
}
